import React, { useState, useEffect } from "react";
import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import LoginHeader from "./components/LoginHeader.js";
import SideNavigation from "./components/SideNavigation.js";
import AADHeader from "./components/AADHeader.js";
import { useNavigate, useLocation } from "react-router-dom";
import { Card } from "react-bootstrap";
import HighchartsExporting from "highcharts/modules/exporting";
import HighchartsOfflineExporting from "highcharts/modules/offline-exporting";
import HighchartsFullscreen from "highcharts/modules/full-screen";
import HighchartsMap from "highcharts/modules/map"; // Import the map module
import worldMap from "@highcharts/map-collection/custom/world.geo.json";
import highchartsMore from "highcharts/highcharts-more";
import { Typography, Backdrop, Breadcrumbs, Link, CircularProgress } from "@mui/material";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import mapDataWorld from "@highcharts/map-collection/custom/world.geo.json"

HighchartsMap(Highcharts);
highchartsMore(Highcharts);
HighchartsExporting(Highcharts);
HighchartsOfflineExporting(Highcharts);
HighchartsFullscreen(Highcharts);

const countryNameMap = {
  "United States of America": "United States",
  "Russia": "Russian Federation",
  "South Korea": "Korea, Republic of",
  // Add more if needed
  "Austria": "Austria",
  "India": "India"
};

const ECDashboard = () => {
  const navigate = useNavigate();
  const username = localStorage.getItem("userName");
  const companyid = localStorage.getItem("companyid");
  const [isSidebarOpen, setIsSidebarOpen] = useState(window.innerWidth >= 768);
  const [loading, setLoading] = useState(false);
  const [dashboardData, setDashboardData] = useState("");
  const location = useLocation(); // To get query params

  const getQueryParams = (search) => {
    const params = new URLSearchParams(search);
    return {
      from: params.get("from"), // For example: ?from=2024-08-01
      to: params.get("to"), // For example: ?to=2024-08-31
      rowKey: params.get("rowData"), // For example: ?rowKey=123
    };
  };

  const { from, to, rowKey } = getQueryParams(location.search);

  const toggleSidebar = () => {
    setIsSidebarOpen((prev) => !prev);
  };

  useEffect(() => {
    console.log("From:", from);
    console.log("To:", to);
    fetchDashboard();
    const handleResize = () => {
      if (window.innerWidth < 768) {
        setIsSidebarOpen(false);
      } else {
        setIsSidebarOpen(true);
      }
    };

    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, [from, to, rowKey]);

  const renderHeader = () => {
    const loginType = localStorage.getItem("loginType");
    if (loginType === "AAD") {
      return <AADHeader />;
    }
    return <LoginHeader toggleSidebar={toggleSidebar} isSidebarOpen={isSidebarOpen} heading="" />;
  };

  const pieChartOptions = {
    chart: {
      type: "pie",
    },
    title: {
      text: "Survey response",
      align: "center",
    },
    credits: {
      enabled: false,
    },
    tooltip: {
      enabled: false, // Disable tooltip to avoid additional text
    },
    plotOptions: {
      pie: {
        allowPointSelect: true,
        cursor: "pointer",
        dataLabels: {
          enabled: true,
          format: "{point.percentage:.1f}%", // Show only the percentage
          distance: 20, // Distance outside the pie chart
          style: {
            fontSize: "14px",
            fontWeight: "bold",
            color: "#333",
          },
          connectorShape: "crookedLine", // Creates a line connecting the label to the pie slice
          crookDistance: "70%", // Controls the bend in the connector line (makes the line more like an arrow)
          connectorPadding: 5,
          connectorWidth: 1.5,
          connectorColor: "#333",
        },
        showInLegend: true,
        borderColor: null,
      },
    },
    series: [
      {
        name: "Response",
        colorByPoint: true,
        data: [
          {
            name: "Responded",
            y: dashboardData.surveyResponse?.Responded || 0,
            color: "#B4A34A",
          },
          {
            name: "Not responded",
            y: dashboardData.surveyResponse?.NotResponded || 0,
            color: "#FAB430",
          },
        ],
      },
    ],
    legend: {
      align: "center",
      verticalAlign: "bottom",
      layout: "horizontal",
      itemStyle: {
        fontSize: "14px",
        fontWeight: "bold",
        color: "#333",
      },
      symbolRadius: 0, // Makes legend square instead of circle
      symbolHeight: 14,
      symbolWidth: 14,
    },
  };

  const transportModePreferences = dashboardData.transportModePreferences || {};
  const transportCategories = Object.keys(transportModePreferences);
  const transportData = Object.values(transportModePreferences);
  const transportModeOptions = {
    chart: {
      type: "bar",
    },
    title: {
      text: "Transport mode preferences",
    },
    credits: {
      enabled: false,
    },
    xAxis: {
      categories: transportCategories,
    },
    yAxis: {
      allowDecimals: false,
      tickInterval: 1,
      min: 0,
      title: { text: "Values" },
    },
    series: [
      {
        name: "Preferences",
        data: transportData,
        color: "#B4A34A",
      },
    ],
    legend: {
      enabled: false, // This will hide the legend
    },
  };

  const getCountryCode = (countryName) => {
    const mapFeatures = worldMap.features; // Features contain countries and their properties
    for (const feature of mapFeatures) {
      if (feature.properties.name === countryName) {
        return feature.properties["iso-a2"]; // Return the ISO-2 code if found
      }
    }
    // Try alternative country name mappings
    const countryMappings = {
      "United States": "US",
      "Austria": "AT",
      "India": "IN"
    };
    return countryMappings[countryName] || null;
  };
  // Extract countries and their response rates
  const countryResponseRates = dashboardData.countryLevelResponseRate || {};

  // Only use API data for max calculation
  const apiCountryValues = Object.values(countryResponseRates);
  console.log(apiCountryValues);
  const maxCountryValue = Math.max(...Object.values(countryResponseRates));

  const allCountriesData = worldMap.features
    .filter(feature => feature.properties["iso-a2"] && feature.properties["iso-a2"].length === 2)
    .map(feature => {
      const mapCountryName = feature.properties.name;
      // Map API name to map name
      const apiCountryName = countryNameMap[mapCountryName] || mapCountryName;
      const countryCode = feature.properties["iso-a2"];
      const apiResponseCount = countryResponseRates[apiCountryName];
      if (apiResponseCount && maxCountryValue) {
        const percent = Math.round((apiResponseCount / maxCountryValue) * 100);
        return {
          code: countryCode,
          value: percent,
          name: mapCountryName
        };
      } else {
        return {
          code: countryCode,
          value: null,
          name: mapCountryName
        };
      }
    });

  const countryResponseRateOptions = {
    chart: { map: "custom/world" },
    title: { text: "Country-level response rate" },
    credits: { enabled: false },
    mapNavigation: {
      enabled: true,
      enableButtons: true,
      buttonOptions: {
        verticalAlign: 'bottom'
      }
    },
    colorAxis: {
      min: 0,
      max: 100,
      minColor: "#fab430",
      maxColor: "#407775",
      labels: { format: '{value}%' }
    },
    legend: {
      layout: 'horizontal',
      align: 'center',
      verticalAlign: 'bottom',
      symbolWidth: 120
    },
    series: [{
      name: "Response Rate",
      mapData: worldMap,
      data: allCountriesData,
      joinBy: ["iso-a2", "code"],
      borderColor: "#fff",
      borderWidth: 0.5,
      nullColor: "#E0E0E0",
      states: { hover: { color: "#BADA55" } },
      dataLabels: { enabled: false },
      tooltip: { pointFormat: "{point.name}: {point.value}%" }
    }]
  };

  const siteResponse = dashboardData.siteLevelResponseRate || {};
  const siteResponseCategories = Object.keys(siteResponse);
  const siteResponseData = Object.values(siteResponse);
  const siteResponseRateOptions = {
    chart: {
      type: "bar",
    },
    credits: {
      enabled: false,
    },
    title: {
      text: "Site-level response rate",
    },
    xAxis: {
      categories: siteResponseCategories,
    },
    yAxis: {
      allowDecimals: false,
      tickInterval: 1,
      min: 0,
      title: { text: "Values" },
    },
    series: [
      {
        name: "Response Rate",
        data: siteResponseData,
        color: "#9AC7BF",
      },
    ],
    legend: {
      enabled: false,
    },
  };

  async function fetchDashboard() {
    try {
      let response = await fetch(`${process.env.REACT_APP_BASE_API_URL}employee-dashboard`, {
        method: "POST",
        headers: {
          "Content-type": "application/json; charset=UTF-8",
        },
        body: JSON.stringify({
          companyId: companyid,
          username: username,
          surveyFromDate: from,
          surveyToDate: to,
          rowKey: rowKey,
        }),
      });
      if (response.status === 200) {
        const responseJson = await response.json();
        if (responseJson.success == false) {
        } else if (responseJson.status == 400) {
        } else {
          console.log(responseJson);
          setDashboardData(responseJson.data);
        }
      } else {
      }
    } catch (error) {
      console.error(error);
    }
  }

  return (
    <div id="wrapper">
      <div>
        {loading && (
          <Backdrop sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }} open={true}>
            <CircularProgress color="inherit" />
          </Backdrop>
        )}
      </div>
      {renderHeader()}
      <SideNavigation isSidebarOpen={isSidebarOpen} toggleSidebar={toggleSidebar} />
      <div id="page-content-wrapper" className={isSidebarOpen ? "page-content-wrapper-pd-0" : "page-wrapper"}>
        <div className="container formpd">
          <div className="row mb-4">
            <div className="col-md-8">
              <div className="d-flex flex-column steps__containt-hader">
                <h1 className="title">Employee Commute</h1>
                <div role="presentation">
                  <Breadcrumbs separator={<NavigateNextIcon fontSize="small" />} aria-label="breadcrumb">
                    <Link underline="hover" color="inherit" href="/forms-apps" className="link">
                      Forms & Apps
                    </Link>
                    <Link underline="hover" color="inherit" href="/employee-commute-data" className="link">
                      Employee Commute Survey
                    </Link>
                    <Typography color="text.primary">Employee Commute Dashboard</Typography>
                  </Breadcrumbs>
                </div>
              </div>
            </div>
          </div>

          {/* Tiles Section */}
          <div className="row ec-dashboard">
            <div className="col-md-4 mb-4">
              <Card className="ec-card">
                <Card.Body>
                  <div className="d-flex">
                    <img src="./assets/img/formsapps/total-recipients.svg" alt="Icon" className="mr-3" />
                    <div>
                      <Card.Title>Total recipients</Card.Title>
                      <Card.Text>{dashboardData.totalRecipients}</Card.Text>
                    </div>
                  </div>
                </Card.Body>
              </Card>
            </div>
            <div className="col-md-4 mb-4">
              <Card className="ec-card">
                <Card.Body>
                  <div className="d-flex">
                    <img src="./assets/img/formsapps/response-rate.svg" alt="Icon" className="mr-3" />
                    <div>
                      <Card.Title>Response rate</Card.Title>
                      <Card.Text>{dashboardData.responseRateInPercentage}%</Card.Text>
                    </div>
                  </div>
                </Card.Body>
              </Card>
            </div>
            <div className="col-md-4 mb-4">
              <Card className="ec-card">
                <Card.Body>
                  <div className="d-flex">
                    <img src="./assets/img/formsapps/survey-conducted.svg" alt="Icon" className="mr-3" />
                    <div>
                      <Card.Title>Survey Conducted</Card.Title>
                      <Card.Text>{dashboardData.totalRecipients}</Card.Text>
                    </div>
                  </div>
                </Card.Body>
              </Card>
            </div>
          </div>

          {/* Charts Section */}
          <div className="row ec-dashboard">
            <div className="col-md-6 mb-4">
              <HighchartsReact highcharts={Highcharts} options={pieChartOptions} />
            </div>
            <div className="col-md-6 mb-4">
              <HighchartsReact highcharts={Highcharts} options={transportModeOptions} />
            </div>
            <div className="col-md-6 mb-4">
              <HighchartsReact highcharts={Highcharts} constructorType={"mapChart"} options={countryResponseRateOptions} />
            </div>
            <div className="col-md-6 mb-4">
              <HighchartsReact highcharts={Highcharts} options={siteResponseRateOptions} />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ECDashboard;
